import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import {
  CreateSparkCampaign,
  optimizationGoalsForObjective,
  type SparkAdGroupOptimizationGoal,
} from '../../../../service/create-spark-campaign/create-spark-campaign';

/** TikTok ad group `bid_type` values offered in the Spark UI. */
export type SparkAdGroupBidTypeOption = 'BID_TYPE_NO_BID' | 'BID_TYPE_CUSTOM';

@Component({
  selector: 'cad-group-bidding-optimization',
  imports: [],
  templateUrl: './group-bidding-optimization.html',
  styleUrl: './group-bidding-optimization.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupBiddingOptimization {
  expanded = signal<boolean>(true);

  public createSparkCampaignService = inject(CreateSparkCampaign);

  readonly allowedOptimizationGoals = computed(() =>
    optimizationGoalsForObjective(this.createSparkCampaignService.currentSparkCampaign()?.objectiveType),
  );

  readonly bidTypeOptions: { value: SparkAdGroupBidTypeOption; label: string }[] = [
    { value: 'BID_TYPE_NO_BID', label: 'Maximum delivery' },
    { value: 'BID_TYPE_CUSTOM', label: 'Custom bid control' },
  ];

  frequencyImpressionsDisplay(): string {
    const v = this.createSparkCampaignService.frequency();
    return v === undefined || v === null ? '' : String(v);
  }

  frequencyScheduleDaysDisplay(): string {
    const v = this.createSparkCampaignService.frequency_schedule();
    return v === undefined || v === null ? '' : String(v);
  }

  onFrequencyImpressionsInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '') {
      this.createSparkCampaignService.frequency.set(undefined);
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) {
      this.createSparkCampaignService.frequency.set(undefined);
      return;
    }
    this.createSparkCampaignService.frequency.set(n < 1 ? 1 : n);
  }

  onFrequencyScheduleDaysInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '') {
      this.createSparkCampaignService.frequency_schedule.set(undefined);
      return;
    }
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) {
      this.createSparkCampaignService.frequency_schedule.set(undefined);
      return;
    }
    this.createSparkCampaignService.frequency_schedule.set(n < 1 ? 1 : n);
  }

  bidPriceDisplay(): string {
    const v = this.createSparkCampaignService.bid_price();
    return Number.isFinite(v) ? String(v) : '';
  }

  onBidPriceInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    if (raw === '') {
      this.createSparkCampaignService.bid_price.set(0);
      return;
    }
    const n = parseFloat(raw);
    this.createSparkCampaignService.bid_price.set(Number.isFinite(n) && n >= 0 ? n : 0);
  }

  formatObjectiveType(type: string | undefined): string {
    if (!type) return '—';
    return type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
  }

  formatOptimizationGoalLabel(goal: SparkAdGroupOptimizationGoal): string {
    return goal
      .split('_')
      .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  isOptimizationGoalSelected(goal: SparkAdGroupOptimizationGoal): boolean {
    return this.createSparkCampaignService.optimization_goal() === goal;
  }

  selectOptimizationGoal(goal: SparkAdGroupOptimizationGoal): void {
    this.createSparkCampaignService.applyOptimizationGoal(
      goal,
      this.createSparkCampaignService.currentSparkCampaign()?.objectiveType,
    );
  }

  optimizationGoalOptionClass(goal: SparkAdGroupOptimizationGoal): string {
    const selected = this.isOptimizationGoalSelected(goal);
    const base =
      'relative w-full rounded-md px-3 py-2 text-left text-sm font-semibold focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2';
    if (selected) {
      return `${base} z-10 bg-primary text-white shadow-sm ring-1 ring-inset ring-primary dark:bg-primary dark:ring-primary`;
    }
    return `${base} bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:ring-gray-700 dark:hover:bg-white/20`;
  }

  bidControlVisible(): boolean {
    const svc = this.createSparkCampaignService;
    if (svc.bid_type() !== 'BID_TYPE_CUSTOM') {
      return false;
    }
    const be = svc.billing_event();
    return be === 'CPC' || be === 'CPM' || be === 'CPV';
  }

  isBidTypeSelected(value: SparkAdGroupBidTypeOption): boolean {
    return this.createSparkCampaignService.bid_type() === value;
  }

  selectBidType(value: SparkAdGroupBidTypeOption): void {
    this.createSparkCampaignService.bid_type.set(value);
  }

  bidTypeOptionClass(value: SparkAdGroupBidTypeOption, index: number): string {
    const selected = this.isBidTypeSelected(value);
    const rounded =
      index === 0 ? 'rounded-l-md' : index === this.bidTypeOptions.length - 1 ? 'rounded-r-md' : '';
    const overlap = index > 0 ? '-ml-px' : '';
    const base =
      `relative flex min-w-0 flex-1 basis-0 items-center justify-center px-3 py-2 text-sm font-semibold focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${overlap} ${rounded}`;
    if (selected) {
      return `${base} z-10 bg-primary text-white shadow-sm inset-ring-1 inset-ring-primary dark:bg-primary dark:inset-ring-primary`;
    }
    return `${base} bg-white text-gray-900 inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20`;
  }
}
